import { defineStore } from 'pinia';
import type { PollEntry, PollConfig, PollKey, PollResult } from '../models/Poll';

const LOCAL_STORAGE_RUNNING_POLLS_KEY = 'runningPolls';

export const usePollingStore = defineStore('polling', {
    state: () => ({
        polls: {} as Partial<Record<PollKey, PollEntry<unknown>>>,
        results: {} as Partial<Record<PollKey, PollResult<unknown>>>,
    }),
    actions: {
        register<T>(key: PollKey, config: PollConfig<T>) {
            this.polls[key] = { config: config as PollConfig<T>, running: false, timerId: undefined };
            if (!this.results[key]) this.clearResults(key);

            /**
             * If the key was already running, start it immediately.
             * This is needed when the page is refreshed and polling
             * should continue with the refreshed state.
             */
            const runningPolls = JSON.parse(localStorage.getItem(LOCAL_STORAGE_RUNNING_POLLS_KEY) ?? '[]') as PollKey[];
            if (runningPolls.includes(key)) {
                this.start(key, true);
            }
        },
        start(key: PollKey, requireRestart: boolean = false) {
            const poll = this.polls[key];

            if (!poll || (poll.running && !requireRestart))
                return; // Poll is already running... so no need to start it again

            if (requireRestart) {
                // To make sure we don't have multiple timers running
                this.stop(key);
            }

            poll.running = true;
            this.clearResults(key);

            const __run = async () => {
                try {
                    // Call the API
                    const data = await poll.config.api();

                    // If the data is undefined, don't add it to the results
                    if (!data) return;

                    // Add the data to the results
                    this.results[key] = { data, timestamp: Date.now() };

                } catch (error) {
                    // Do not add any data in the latest results
                    // Only add the error..
                    this.results[key] = { error, timestamp: Date.now() };

                    // Call the onError callback if provided
                    poll.config.onError?.(error);
                }

                /**
                 * If the poll is still running, set the timeout to run again
                 */
                if (poll.running) {
                    poll.timerId = setTimeout(__run, poll.config.interval);
                    this.saveRunningPolls(); // Save the running polls to the local storage
                }
            }

            // Kick off now!
            __run();

        },
        saveRunningPolls() {
            const runningPolls = Object.entries(this.polls)
                .filter(([, entry]) => entry?.running)
                .map(([key]) => key as PollKey);

            if (runningPolls.length) {
                localStorage.setItem(LOCAL_STORAGE_RUNNING_POLLS_KEY, JSON.stringify(runningPolls));
            } else {
                localStorage.removeItem(LOCAL_STORAGE_RUNNING_POLLS_KEY);
            }
        },
        stop(key: PollKey) {
            const poll = this.polls[key];
            if (!poll) return;

            poll.running = false;
            if (poll.timerId) {
                // Clear the timeout to avoid memory leaks
                clearTimeout(poll.timerId);
                poll.timerId = undefined;
            }
            this.saveRunningPolls(); // Save the remaining running polls to the local storage
        },
        clearResults(key: PollKey) {
            this.results[key] = undefined as unknown as PollResult<unknown>;
        },
        stopAll() {
            Object.keys(this.polls).forEach(key => this.stop(key as PollKey));
        },
    },
});